import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getLiveEvents, getVisitLogs, getLiveEventCompletion } from '../services/api';
import {
  Box,
  Typography,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Link
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { format } from 'date-fns';

interface TicketType {
  [key: string]: string;
}

interface LiveEvent {
  id: number;
  title: string;
  venue: string;
  start_at: string;
  open_at: string | null;
  special_instructions?: string | null;
  reference_url?: string | null;
  ticket_types?: TicketType | string | null;
  door_open_time?: string | null;
  performance_start_time?: string | null;
  ticket_sales_status?: string | null;
  special_event_info?: string | null;
}

interface VisitLog {
  id: number;
  live_event: number;
  live_event_title: string;
  points_awarded: number;
  created_at: string;
}

interface CompletionStatus {
  total_events: number;
  attended_events: number;
  completion_rate: number;
  is_perfect: boolean;
}

const StampCard: React.FC = () => {
  const { token, refreshTokenFunction } = useAuth();
  const [events, setEvents] = useState<LiveEvent[]>([]);
  const [visitLogs, setVisitLogs] = useState<VisitLog[]>([]);
  const [completion, setCompletion] = useState<CompletionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchData = async () => {
    if (!token) return;

    try {
      setLoading(true);
      
      const [eventsRes, logsRes, completionRes] = await Promise.all([
        getLiveEvents(token),
        getVisitLogs(token),
        getLiveEventCompletion(token)
      ]);

      setEvents(eventsRes.data);
      setVisitLogs(logsRes.data);
      setCompletion(completionRes.data);
      setError('');
    } catch (err: any) {
      if (err.response && err.response.status === 401) {
        try {
          const newToken = await refreshTokenFunction();
          if (newToken) {
            const [eventsRes, logsRes, completionRes] = await Promise.all([
              getLiveEvents(newToken),
              getVisitLogs(newToken),
              getLiveEventCompletion(newToken)
            ]);
            setEvents(eventsRes.data);
            setVisitLogs(logsRes.data);
            setCompletion(completionRes.data);
            return;
          }
        } catch (refreshError) {}
      }
      setError('データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [token]);

  const isAttended = (eventId: number) => {
    return visitLogs.some(log => log.live_event === eventId);
  };

  // スタンプカードを5個ずつに分割
  const stampCardsData = () => {
    const reversed = [...events].reverse();
    const cards = [];
    for (let i = 0; i < reversed.length; i += 5) {
      cards.push(reversed.slice(i, i + 5));
    }
    
    // 最後のカードが5個未満の場合、点線スタンプで埋める
    if (cards.length > 0) {
      const lastCard = cards[cards.length - 1];
      while (lastCard.length < 5) {
        lastCard.push({ id: -1, title: '', venue: '', start_at: '', open_at: null } as any);
      }
    }
    
    return cards;
  };

  if (loading) {
    return (
      <Box sx={{ marginTop: 6, maxHeight: '80vh', overflowY: 'auto', px: 2, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  const cardPages = stampCardsData();

  return (
    <Box sx={{ 
      pt: { xs: 7, sm: 8 },
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      minHeight: '80vh',
      width: '80%'
    }}>
      {/* タイトル・エラー・参加率 */}
      <Box sx={{
        width: '100%',
        maxWidth: { xs: '100%', sm: 600 },
        pb: 2,
        px: { xs: 1, sm: 2 }
      }}>
        <Typography variant="h6" gutterBottom align="center" sx={{ 
          mb: 3, 
          fontSize: '1.3rem',
          fontWeight: 'bold',
          color: '#d81b60',
          letterSpacing: 1,
          textShadow: '0 2px 8px rgba(216, 27, 96, 0.1)'
        }}>
          来店スタンプカード
        </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {completion && (
        <>
          {/* 参加率表示 - シンプル背景 */}
          <Box sx={{ 
            mb: 2, 
            maxWidth: 320, 
            mx: 'auto',
            textAlign: 'center'
          }}>
            <Typography variant="body2" sx={{ 
              fontSize: '0.9rem', 
              fontWeight: 'bold',
              color: '#d81b60',
              mb: 0.75,
              letterSpacing: 1,
              textShadow: '0 1px 3px rgba(216, 27, 96, 0.1)'
            }}>
              参加率
            </Typography>
            
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'flex-end', 
              justifyContent: 'center',
              gap: 0.5,
              mb: 0.75,
              animation: 'pulse-scale 2s ease-in-out infinite',
              '@keyframes pulse-scale': {
                '0%, 100%': { transform: 'scale(1)' },
                '50%': { transform: 'scale(1.02)' },
              }
            }}>
              <Typography variant="h4" sx={{ 
                fontSize: '2.2rem', 
                fontWeight: 'bold',
                color: '#d81b60',
                lineHeight: 1
              }}>
                {completion.attended_events}
              </Typography>
              <Typography variant="h6" sx={{ 
                fontSize: '1.2rem', 
                color: '#f06292',
                fontWeight: 'bold',
                mb: 0.3
              }}>
                /
              </Typography>
              <Typography variant="h4" sx={{ 
                fontSize: '2.2rem', 
                fontWeight: 'bold',
                color: '#f06292'
              }}>
                {completion.total_events}
              </Typography>
            </Box>

            <Box sx={{ 
              position: 'relative',
              width: '100%',
              height: '8px',
              background: 'rgba(240, 98, 146, 0.15)',
              borderRadius: '4px',
              mb: 0.75,
              overflow: 'hidden'
            }}>
              <Box sx={{ 
                position: 'absolute',
                left: 0,
                top: 0,
                height: '100%',
                width: `${completion.completion_rate}%`,
                background: 'linear-gradient(90deg, #f06292 0%, #ff6f00 100%)',
                borderRadius: '4px',
                transition: 'width 0.6s ease',
                boxShadow: '0 0 8px rgba(255, 111, 0, 0.4)'
              }} />
            </Box>

            <Typography variant="body1" sx={{ 
              fontSize: '1.3rem', 
              fontWeight: 'bold',
              color: '#ff6f00',
              textShadow: '0 2px 4px rgba(255, 111, 0, 0.2)',
              mb: completion.is_perfect ? 1 : 0
            }}>
              {completion.completion_rate.toFixed(1)}%
            </Typography>

            {completion.is_perfect && (
              <Box sx={{
                mt: 2,
                p: 1.5,
                background: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)',
                border: '2px solid #ff6f00',
                borderRadius: 2,
                textAlign: 'center',
                animation: 'bounce 1s ease-in-out infinite',
                '@keyframes bounce': {
                  '0%, 100%': { transform: 'translateY(0)' },
                  '50%': { transform: 'translateY(-4px)' },
                }
              }}>
                <EmojiEventsIcon sx={{ fontSize: 36, color: '#ff6f00', mb: 0.5 }} />
                <Typography variant="body2" fontWeight="bold" sx={{ 
                  fontSize: '0.85rem', 
                  color: '#ff6f00',
                  letterSpacing: 1
                }}>
                  全通達成！
                </Typography>
              </Box>
            )}
          </Box>
        </>
      )}

      {events.length === 0 && (
        <Typography color="text.secondary" textAlign="center" py={4}>
          公開されているライブがありません
        </Typography>
      )}
      </Box>

      {/* スタンプカード（複数ページ対応） */}
      {completion && cardPages.length > 0 && (
        <Box sx={{
          width: '100%',
          px: { xs: 1, sm: 2 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 3
        }}>
          {cardPages.map((cardEvents, pageIndex) => (
            <Box
              key={pageIndex}
              sx={{
                width: '100%',
                px: { xs: 1, sm: 2 },
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}
            >
              {/* スタンプカード背景 */}
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  maxWidth: { xs: '100%', sm: 'calc(100% - 16px)' },
                  aspectRatio: '16 / 10',
                  backgroundImage: 'url(/image/stampcard.png)',
                  backgroundSize: '100% 100%',
                  backgroundPosition: 'top left',
                  backgroundRepeat: 'no-repeat',
                  border: 'none',
                  borderRadius: 2,
                  boxShadow: '0 12px 32px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.6)',
                  overflow: 'visible',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 16px 40px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.6)',
                    transform: 'translateY(-4px)',
                  }
                }}
              >
                {/* スタンプ位置の定義（絶対位置） */}
                {cardEvents.map((event, idx) => {
                  const isEmptySlot = event && event.id === -1;
                  // 元の座標は800x600のサイズを基準にしていたもの
                  // 実際の背景画像は1667x1250なので、それぞれ約2.08倍にスケール
                  const originalPositions = [
                    { left: 32, top: 352 },   // 1つ目
                    { left: 182, top: 352 },  // 2つ目
                    { left: 332, top: 352 },  // 3つ目
                    { left: 482, top: 352 },  // 4つ目
                    { left: 632, top: 352 },  // 5つ目
                  ];
                  
                  const scaleX = 1667 / 800;  // 2.08375
                  const scaleY = 1250 / 600;  // 2.08333
                  const stampSizeScale = (100 / 800) * 1667;  // スタンプサイズを縮小
                  const frameSize = 135;  // 元の枠サイズ
                  const stampSize = 100;  // 現在のスタンプサイズ
                  const centerOffset = (frameSize - stampSize) / 2;  // 中央配置のオフセット: 17.5px

                  if (idx >= originalPositions.length) return null;

                  const origPos = originalPositions[idx];
                  // 枠の中央にスタンプを配置するためのオフセットを追加
                  const scaledLeft = (origPos.left + centerOffset) * scaleX;
                  const scaledTop = (origPos.top + centerOffset) * scaleY;
                  const scaledStampSize = stampSize * Math.max(scaleX, scaleY);
                  
                  const percentLeft = `${(scaledLeft / 1667) * 100}%`;
                  const percentTop = `${(scaledTop / 1250) * 100}%`;
                  const stampWidthPercent = `${(scaledStampSize / 1667) * 100}%`;
                  const stampHeightPercent = `${(scaledStampSize / 1250) * 100}%`;

                  const attended = !isEmptySlot && isAttended(event.id);

                  if (isEmptySlot) {
                    // 点線スタンプ（登録なし）
                    return (
                      <Box
                        key={`dotted-${pageIndex}-${idx}`}
                        sx={{
                          position: 'absolute',
                          left: percentLeft,
                          top: percentTop,
                          width: stampWidthPercent,
                          height: stampHeightPercent,
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          border: '2px dashed rgba(216, 27, 96, 0.2)',
                          borderRadius: '50%'
                        }}
                      />
                    );
                  }

                  if (!event) {
                    return null;
                  }

                  // 登録済みのスタンプ
                  return (
                    <Box
                      key={event.id}
                      sx={{
                        position: 'absolute',
                        left: percentLeft,
                        top: percentTop,
                        width: stampWidthPercent,
                        height: stampHeightPercent,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        flexDirection: 'column',
                        gap: '1px'
                      }}
                      title={`${event.title} (${format(new Date(event.start_at), 'MM/dd')})`}
                    >
                      {attended ? (
                        // 参加済み：スタンプ画像と日付テキストを表示
                        <>
                          <Box
                            sx={{
                              position: 'absolute',
                              width: '100%',
                              height: '100%',
                              backgroundImage: 'url(/image/stamp.png)',
                              backgroundSize: 'contain',
                              backgroundPosition: 'center',
                              backgroundRepeat: 'no-repeat',
                              zIndex: 2
                            }}
                          />
                          {/* 日付テキスト */}
                          <Typography
                            sx={{
                              position: 'absolute',
                              bottom: '8%',
                              zIndex: 3,
                              fontSize: 'clamp(6px, 1.5vw, 14px)',
                              fontWeight: 'bold',
                              color: '#00000061',
                              textAlign: 'center',
                              lineHeight: 1.1
                            }}
                          >
                            {format(new Date(event.start_at), 'MM/dd')}
                          </Typography>
                        </>
                      ) : (
                        // 未参加：点線枠のみ表示
                        <>
                          {/* 日付テキスト */}
                          <Typography
                            sx={{
                              position: 'relative',
                              zIndex: 2,
                              fontSize: 'clamp(6px, 1.5vw, 14px)',
                              fontWeight: 'bold',
                              color: '#9e9e9e',
                              textAlign: 'center',
                              lineHeight: 1.1,
                              opacity: 0.5
                            }}
                          >
                            {format(new Date(event.start_at), 'MM/dd')}
                          </Typography>

                          {/* 外側の点線円枠 */}
                          <Box
                            sx={{
                              position: 'absolute',
                              width: '100%',
                              height: '100%',
                              border: '2px dashed #bdbdbd',
                              borderRadius: '50%',
                              opacity: 0.5
                            }}
                          />
                        </>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {/* ライブイベント情報一覧 */}
      {events.length > 0 && (
        <Box sx={{
          width: '100%',
          maxWidth: { xs: '100%', sm: 700 },
          mt: 4,
          px: { xs: 1, sm: 2 },
          pb: 4
        }}>
          <Typography variant="h6" gutterBottom sx={{ 
            mb: 2, 
            fontSize: '1.2rem',
            fontWeight: 'bold',
            color: '#d81b60',
            letterSpacing: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <InfoOutlinedIcon /> ライブ詳細情報
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {events.map((event) => {
              const attended = isAttended(event.id);
              const hasAdditionalInfo = event.special_instructions || 
                                        event.reference_url || 
                                        event.ticket_types ||
                                        event.door_open_time ||
                                        event.performance_start_time ||
                                        event.ticket_sales_status ||
                                        event.special_event_info;

              // ticket_typesをパース
              let ticketTypesObj: TicketType | null = null;
              if (event.ticket_types) {
                if (typeof event.ticket_types === 'string') {
                  try {
                    ticketTypesObj = JSON.parse(event.ticket_types);
                  } catch (e) {
                    ticketTypesObj = null;
                  }
                } else {
                  ticketTypesObj = event.ticket_types;
                }
              }

              return (
                <Accordion 
                  key={event.id}
                  sx={{
                    background: attended 
                      ? 'linear-gradient(135deg, rgba(255, 111, 0, 0.05) 0%, rgba(255, 111, 0, 0.02) 100%)'
                      : 'linear-gradient(135deg, rgba(189, 189, 189, 0.05) 0%, rgba(189, 189, 189, 0.02) 100%)',
                    border: `2px solid ${attended ? '#ff6f00' : '#e0e0e0'}`,
                    borderRadius: '8px !important',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    '&:before': { display: 'none' },
                    '&.Mui-expanded': {
                      margin: 0,
                      boxShadow: '0 4px 16px rgba(0,0,0,0.12)'
                    }
                  }}
                >
                  <AccordionSummary
                    expandIcon={hasAdditionalInfo ? <ExpandMoreIcon sx={{ color: attended ? '#ff6f00' : '#757575' }} /> : null}
                    sx={{
                      '& .MuiAccordionSummary-content': {
                        my: 1.5,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.5
                      },
                      '&.Mui-expanded': {
                        minHeight: 48
                      },
                      cursor: hasAdditionalInfo ? 'pointer' : 'default'
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%' }}>
                      <Box sx={{ flex: 1, pr: 1 }}>
                        <Typography variant="body1" sx={{ 
                          fontWeight: 'bold', 
                          color: attended ? '#ff6f00' : '#424242',
                          fontSize: '1rem',
                          mb: 0.5,
                          lineHeight: 1.3
                        }}>
                          {event.title}
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                          <Typography variant="caption" sx={{ 
                            color: attended ? '#ff8f00' : '#757575',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            fontSize: '0.8rem'
                          }}>
                            📅 {format(new Date(event.start_at), 'yyyy/MM/dd HH:mm')}
                          </Typography>
                          {hasAdditionalInfo && (
                            <Chip 
                              label="詳細あり" 
                              size="small" 
                              icon={<InfoOutlinedIcon sx={{ fontSize: 14 }} />}
                              sx={{ 
                                height: 20, 
                                fontSize: '0.7rem',
                                background: 'linear-gradient(135deg, #42a5f5 0%, #1e88e5 100%)',
                                color: 'white',
                                fontWeight: 'bold'
                              }} 
                            />
                          )}
                        </Box>
                        {event.venue && (
                          <Typography variant="caption" sx={{ 
                            color: attended ? '#ff8f00' : '#757575',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            mt: 0.5,
                            fontSize: '0.8rem'
                          }}>
                            📍 {event.venue}
                          </Typography>
                        )}
                      </Box>
                      
                      <Chip
                        label={attended ? '参加済' : '未参加'}
                        color={attended ? 'success' : 'default'}
                        variant={attended ? 'filled' : 'outlined'}
                        size="small"
                        sx={{
                          fontWeight: 'bold',
                          fontSize: '0.7rem',
                          height: 24,
                          minWidth: 60,
                          ...(attended && {
                            background: 'linear-gradient(135deg, #81c784 0%, #66bb6a 100%)',
                            color: 'white'
                          }),
                          ...(!attended && {
                            borderColor: '#bdbdbd',
                            color: '#9e9e9e'
                          })
                        }}
                      />
                    </Box>
                  </AccordionSummary>

                  {hasAdditionalInfo && (
                    <AccordionDetails sx={{ pt: 0, pb: 2, px: 2 }}>
                      <Divider sx={{ mb: 2 }} />
                      
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {/* チケット販売状況 */}
                        {event.ticket_sales_status && (
                          <Box sx={{ 
                            p: 1.5, 
                            bgcolor: 'rgba(33, 150, 243, 0.08)', 
                            borderRadius: 1,
                            borderLeft: '4px solid #2196f3'
                          }}>
                            <Typography variant="caption" sx={{ 
                              color: '#1976d2', 
                              fontWeight: 'bold',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              mb: 0.5
                            }}>
                              <ConfirmationNumberIcon sx={{ fontSize: 16 }} />
                              チケット販売状況
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#424242', pl: 2.5 }}>
                              {event.ticket_sales_status}
                            </Typography>
                          </Box>
                        )}

                        {/* チケット種別 */}
                        {ticketTypesObj && Object.keys(ticketTypesObj).length > 0 && (
                          <Box sx={{ 
                            p: 1.5, 
                            bgcolor: 'rgba(156, 39, 176, 0.08)', 
                            borderRadius: 1,
                            borderLeft: '4px solid #9c27b0'
                          }}>
                            <Typography variant="caption" sx={{ 
                              color: '#7b1fa2', 
                              fontWeight: 'bold',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              mb: 0.5
                            }}>
                              <ConfirmationNumberIcon sx={{ fontSize: 16 }} />
                              チケット種別
                            </Typography>
                            <Box sx={{ pl: 2.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              {Object.entries(ticketTypesObj).map(([type, price]) => (
                                <Typography key={type} variant="body2" sx={{ color: '#424242' }}>
                                  • {type}: <strong>{price}</strong>
                                </Typography>
                              ))}
                            </Box>
                          </Box>
                        )}

                        {/* 開場・開演時刻 */}
                        {(event.door_open_time || event.performance_start_time) && (
                          <Box sx={{ 
                            p: 1.5, 
                            bgcolor: 'rgba(255, 152, 0, 0.08)', 
                            borderRadius: 1,
                            borderLeft: '4px solid #ff9800'
                          }}>
                            <Typography variant="caption" sx={{ 
                              color: '#ef6c00', 
                              fontWeight: 'bold',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              mb: 0.5
                            }}>
                              <AccessTimeIcon sx={{ fontSize: 16 }} />
                              開場・開演時刻
                            </Typography>
                            <Box sx={{ pl: 2.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              {event.door_open_time && (
                                <Typography variant="body2" sx={{ color: '#424242' }}>
                                  • 開場: <strong>{event.door_open_time}</strong>
                                </Typography>
                              )}
                              {event.performance_start_time && (
                                <Typography variant="body2" sx={{ color: '#424242' }}>
                                  • 開演: <strong>{event.performance_start_time}</strong>
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        )}

                        {/* 特別イベント情報 */}
                        {event.special_event_info && (
                          <Box sx={{ 
                            p: 1.5, 
                            bgcolor: 'rgba(103, 58, 183, 0.08)', 
                            borderRadius: 1,
                            borderLeft: '4px solid #673ab7'
                          }}>
                            <Typography variant="caption" sx={{ 
                              color: '#5e35b1', 
                              fontWeight: 'bold',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              mb: 0.5
                            }}>
                              <EmojiEventsIcon sx={{ fontSize: 16 }} />
                              特別イベント情報
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#424242', pl: 2.5 }}>
                              {event.special_event_info}
                            </Typography>
                          </Box>
                        )}

                        {/* 当日の注意事項 */}
                        {event.special_instructions && (
                          <Box sx={{ 
                            p: 1.5, 
                            bgcolor: 'rgba(244, 67, 54, 0.08)', 
                            borderRadius: 1,
                            borderLeft: '4px solid #f44336'
                          }}>
                            <Typography variant="caption" sx={{ 
                              color: '#d32f2f', 
                              fontWeight: 'bold',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              mb: 0.5
                            }}>
                              <WarningAmberIcon sx={{ fontSize: 16 }} />
                              当日の注意事項
                            </Typography>
                            <Typography variant="body2" sx={{ 
                              color: '#c62828', 
                              pl: 2.5,
                              fontWeight: 'bold'
                            }}>
                              {event.special_instructions}
                            </Typography>
                          </Box>
                        )}

                        {/* 参考URL */}
                        {event.reference_url && (
                          <Box sx={{ 
                            p: 1.5, 
                            bgcolor: 'rgba(0, 150, 136, 0.08)', 
                            borderRadius: 1,
                            borderLeft: '4px solid #009688'
                          }}>
                            <Typography variant="caption" sx={{ 
                              color: '#00796b', 
                              fontWeight: 'bold',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              mb: 0.5
                            }}>
                              <OpenInNewIcon sx={{ fontSize: 16 }} />
                              参考URL
                            </Typography>
                            <Link 
                              href={event.reference_url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              sx={{ 
                                color: '#00897b',
                                fontWeight: 'bold',
                                fontSize: '0.85rem',
                                pl: 2.5,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.5,
                                textDecoration: 'none',
                                '&:hover': {
                                  textDecoration: 'underline'
                                }
                              }}
                            >
                              {event.reference_url.length > 50 
                                ? event.reference_url.substring(0, 50) + '...' 
                                : event.reference_url}
                              <OpenInNewIcon sx={{ fontSize: 14 }} />
                            </Link>
                          </Box>
                        )}
                      </Box>
                    </AccordionDetails>
                  )}
                </Accordion>
              );
            })}
          </Box>

          {/* AI抽出に関する注意書き */}
          <Alert 
            severity="info" 
            icon={<InfoOutlinedIcon />}
            sx={{ 
              mt: 3,
              bgcolor: 'rgba(33, 150, 243, 0.08)',
              border: '1px solid rgba(33, 150, 243, 0.3)',
              '& .MuiAlert-icon': {
                color: '#1976d2'
              }
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
              📝 ライブ情報について
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.5 }}>
              上記の詳細情報は、AIを使用して自動抽出されたものであり、
              誤りが含まれる可能性があります。
              <strong style={{ color: '#d32f2f' }}>正確な情報は、必ず公式のチケットサイトや主催者の案内をご確認ください。</strong>
            </Typography>
          </Alert>
        </Box>
      )}
    </Box>
  );
};

export default StampCard;
